import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ClientError, gql } from "graphql-request";
import { Variables } from "graphql-request/src/types";
import { useAlerts } from "context/AlertContext";
import { services } from "services";
import { AlertSeverity } from "utils/constants/alerts.constants";
import { QueryAPIKey } from "utils/constants/api.constants";
import handleRetryFailure from "utils/helpers/handleRetryFailure";
import { TReactQueryObserverOptions } from "utils/types";
import { IProjectGQLResponseData } from "./useProjectData.models";

export const query = gql`
  query ProjectData($projectId: String!) {
    summary {
      project(projectId: $projectId) {
        alias
        active
        id
        name
        status
        type
        templateType
        sourceArn
        metadata
        previousStatus
        lastModifiedDate
        members {
          userId
          roles
        }
        ... on Reliance {
          regulatoryEvent
          eventTypes
          sponsor {
            id
            name
            tenantId
          }
        }
        ... on RegulatoryReview {
          regulatoryEvent
          eventTypes
          sponsor {
            id
            name
            tenantId
          }
        }
        ... on SharedWorkspace {
          regulatoryEvent
          eventTypes
          configuration {
            hasReferenceHA
            contentUploadCards
          }
          sponsor {
            id
            name
            tenantId
          }
        }
      }
    }
  }
`;

const useProjectData = (
  projectId: string | undefined,
  options?: TReactQueryObserverOptions<IProjectGQLResponseData, ClientError>,
) => {
  const { t } = useTranslation("common", { keyPrefix: "notifications" });

  const { showAlert } = useAlerts();

  const { data, isLoading, isFetching, error } = useQuery<
    IProjectGQLResponseData,
    ClientError
  >({
    queryKey: [QueryAPIKey.GetProjectData, projectId],
    queryFn: () =>
      services.graphQL.request<IProjectGQLResponseData, Variables>(query, {
        projectId,
      }),
    retry: handleRetryFailure(() =>
      showAlert({
        severity: AlertSeverity.Error,
        message: t("getProjectInformationFailure"),
      }),
    ),
    enabled: !!projectId,
    staleTime: 3000,
    ...options,
  });

  return {
    data: data?.summary.project,
    isLoading,
    isFetching,
    error,
  };
};

export default useProjectData;
